import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';
import type { CapacityCheckResult } from '@tailoring-crm/shared';
import { Button } from '@tailoring-crm/ui';
import { formatDateLong } from '../../lib/format.js';

/**
 * A word in somebody's ear while they are promising a date. Nothing more than that.
 *
 * THIS WARNING BLOCKS NOTHING. It does not disable the save button, it does not add a rule to the
 * page's validation and it does not touch the request the booking sends. A shop that wants to
 * promise Tuesday knowing full well the workshop says Thursday is a shop making a commercial
 * decision, and the software's job there is to have said so, not to argue.
 *
 * IT NEVER CHANGES THE DATE BY ITSELF. The earliest date the engine offers is put into the input
 * only when somebody presses the button that names it, and the input gains no `min` and no
 * auto-correction — a date box that silently rewrites what was typed is worse than no advice.
 *
 * AND IT DISAPPEARS RATHER THAN COMPLAINS. A check that is still loading, that failed, that was
 * refused, or that never fired because the phone is offline renders nothing at all: an advisory
 * that cannot answer has nothing to say, and an error message beside a date box would read as a
 * problem with the booking.
 */

export interface PromisedDateWarningProps {
  /** The answer, or `null` for every state in which no advice may be shown. */
  result: CapacityCheckResult | null;
  /** Called only from the control below, and only when a person presses it. */
  onUseEarliestDate: (day: string) => void;
}

export function PromisedDateWarning({
  result,
  onUseEarliestDate,
}: PromisedDateWarningProps): ReactElement | null {
  const { t } = useTranslation();
  if (!result || result.feasible) return null;

  const earliest = result.earliestDeliveryDay;

  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm"
    >
      <p className="flex items-center gap-2 font-medium">
        <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
        {t('capacity.warning.title')}
      </p>
      {earliest === null ? (
        <p>{t('capacity.warning.noEarliest')}</p>
      ) : (
        <>
          <p>{t('capacity.warning.earliest', { date: formatDateLong(earliest) })}</p>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onUseEarliestDate(earliest)}
            >
              {t('capacity.warning.useEarliest', { date: formatDateLong(earliest) })}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
